import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { IrisApiQueryInitializerService } from '@iris/api-query';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { IrisTimeSandbox } from './common/modules/time';
import { IrisSecurityService } from '@iris/common/modules/security/services/security.service';
import { AuthFacade } from '@iris/modules/auth/utils/auth.facade';
import { TranslateService } from '@ngx-translate/core';
import { IrisLocaleService } from '@iris/common/modules/locale/services/locale.service';
import { IrisLocalStorageService } from '@iris/common/services/utils/iris-local-storage.service';
import { inject } from '@angular/core';
import { IrisUserService, MeSettings } from '@iris/common/services/user.service';
import { IrisUserSettingsService } from '@iris/common/services/user-settings.service';
import { IrisUserSettingsI } from '@iris/common/modules/grid-configuration/models/grid-configuration.settings';
import { IrisEnvironmentService } from '@iris/common/services/environment.service';
import { extractPayloadFromToken } from '@iris/modules/auth/utils/extract-payload-from-token';
import { CommandWebsocketService } from '@iris/common/services/websockets/command-websocket.service';
import { MicroservicesUtils } from '@iris/common/utils/url.utils';
import { NO_AUTH_TOKEN_PATTERNS } from '@iris/common/utils/handlers/no-auth-token-patterns';
import { AuthAutoRefreshTokenService } from '@iris/modules/auth/utils/auth.auto-refresh-token.service';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { LanguageCodes } from '@iris/common/models/IrisLanguage';
import { MsalService } from '@azure/msal-angular';
import { ChunkLoadErrorHandlerService } from '@iris/common/services/system/chunk-load-error-handler.service';

const MICROSERVICE_REDIRECTS: { name: string; redirectUrl: string }[] = Object.entries(MicroservicesUtils.MICROSERVICES)
  .map(
    (entry) =>  ({ name: entry[0], redirectUrl: `http://localhost:${entry[1]}/restful` }),
  );

export function AppInitializer(): Observable<unknown> {
  const envService = inject(IrisEnvironmentService);
  const apiQueryInitializer = inject(IrisApiQueryInitializerService);
  const globalSandbox = inject(IrisGlobalSandbox);
  const timeSandbox = inject(IrisTimeSandbox);
  const securityService = inject(IrisSecurityService);
  const authFacade = inject(AuthFacade);
  const translate = inject(TranslateService);
  const localeService = inject(IrisLocaleService);
  const localStorageService = inject(IrisLocalStorageService);
  const userService = inject(IrisUserService);
  const userSettingsService = inject(IrisUserSettingsService);
  const commandWebsocketService = inject(CommandWebsocketService);
  const autoRefreshTokenService = inject(AuthAutoRefreshTokenService);
  const toastService = inject(ToastService);
  const msalService = inject(MsalService);
  const chunkLoadErrorHandler = inject(ChunkLoadErrorHandlerService);

  chunkLoadErrorHandler.activate();

  const extractUserFromToken = (authToken: string): IdentUserI => {
    const payload = extractPayloadFromToken<{ account_details: string }>(authToken);
    return JSON.parse(payload.account_details);
  };

  const extractBackendVersion = (authToken: string): string | null => {
    const payload = extractPayloadFromToken<{ backendVersion: string }>(authToken);
    return payload?.backendVersion;
  };

  const appInitializerEnv = {
    env: envService.env,
    apiQueryConfig: {
      serverUrl: envService.env.serverUrl,
      apiUrl: envService.env.apiUrl,
      actualToken: () => authFacade.actualToken(),
      urlParams: {
        instanceId: envService.env.instanceId,
        formsUrlPart: envService.env.formsEnabled ? `/forms/${envService.env.instanceId}/` : '',
      },
    },
  };

  apiQueryInitializer.init(
    appInitializerEnv.apiQueryConfig,
    {
      redirectMicroservices: appInitializerEnv.env.redirectMicroservices,
      redirectMicroservicesUrl: MICROSERVICE_REDIRECTS,
    },
    NO_AUTH_TOKEN_PATTERNS,
  );

  const appInitializer$ = authFacade.actualToken().pipe(
    switchMap((authToken) => authFacade.setupToken(authToken)),
    switchMap((authToken) => {
      // prevent initialize before user login
      if (!authFacade.isLoggedIn()) {
        return of(null);
      }

      autoRefreshTokenService.activate();

      const currentUser = extractUserFromToken(authToken);
      userService.setMe(currentUser);
      envService.backendVersion = extractBackendVersion(authToken);

      commandWebsocketService.subscribeToUser(currentUser.id);

      const userSettings$ = userSettingsService.getUserSettingsById<IrisUserSettingsI<MeSettings>>('general', currentUser.id);

      return forkJoin([
        timeSandbox.timeFormats$.pipe(take(1)),
        securityService.getRights().pipe(take(1)),
        userSettings$.pipe(tap(settings => userService.setMeSettings(settings))),
      ]).pipe(
        tap(() => {
          const projectsIds: number[] = userService.meSettings.recentProjects || [];
          globalSandbox.setRecentProjectsIds( projectsIds);

          if (projectsIds[0]) {
            globalSandbox.changeCurrentProjectId(projectsIds[0]);
          }
        }),
        map(() => envService.env),
      );
    }),
  );

  return appInitializer$.pipe(
    switchMap(() => msalService.initialize().pipe(
      catchError((error) => {
        console.error('MSAL initialization failed:', error);
        return of(null);
      }),
      switchMap(() => {
        const navigatorLanguage =  navigator.language.replace(/^([^\-]+).*/, '$1');
        let userLang = localeService.getCurrentLocale() || localStorageService.getItem('defaultAuthLanguage') || navigatorLanguage || 'de';
        if (!authFacade.isLoggedIn() && !(/en|de/is.exec(userLang))) {
          userLang = 'de';
        }
        translate.setFallbackLang(LanguageCodes.EN_SHORT);
        return translate.use(userLang).pipe(
          tap(() => { localStorageService.setItem('defaultAuthLanguage', userLang); }),
          catchError(() => {
            const lastLanguage = localStorageService.getItem('defaultAuthLanguage');
            translate.use(lastLanguage || LanguageCodes.EN_SHORT);
            setTimeout(() => toastService.error({ message: translate.instant('error.LanguageUnavailable') }), 2000);
            return of(null);
          }),
        );
      }),
    )),
  );
}
